import { useEffect, useMemo, useState } from 'react';
import { DATE_RANGES, useDashboard } from "../../contexts/DashboardContext";
import { SummaryStats } from './components/SummaryStats';
import { FilterBar } from './components/FilterBar';
import { getChartsPromises } from '../../services/entity/chart/chart-api';
import { getLineDatasetsFromPoints, splitCharts } from "../../services/entity/chart/chart-service";
import { Loader } from '../../components/loading/Loader';
import { GenericChart } from './components/charts/GenericChart';
import { MainLineChartBlock } from "./components/charts/MainLineChartBlock";
import { BlockLineChart } from "./components/charts/BlockLineChart";

export const Overview = () => {
  const { charts, selectedDateRange } = useDashboard();

  const [isSpecificChartsLoading, setIsSpecificChartsLoading] = useState(false);

  // Contain the data (= InfluxDB points) for each chart
  const [chartsData, setChartsData] = useState([]);

  // Split charts into two arrays: 'summaryCharts' for those in the summary and 'specificCharts' for others
  const [summaryCharts, specificCharts] = useMemo(
    () =>
      splitCharts(
        charts.filter((chart) => chart.workshop === null && chart.workstation === null),
        'overview',
      ),
    [charts],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (specificCharts.length === 0) return;

      setIsSpecificChartsLoading(true);

      if (specificCharts.length > 0 && specificCharts[0].type !== 'donut')
        console.warn('In overview mode, the specific chart should be of type "donut".');

      const resolvedData = await getChartsPromises(specificCharts, selectedDateRange, false);

      setChartsData(resolvedData);
      setIsSpecificChartsLoading(false);
    };

    fetchData();
  }, [specificCharts, selectedDateRange]);

  return (
    <>
      <FilterBar isSpecificChartsLoading={isSpecificChartsLoading} />
      {/*<SummaryStats summaryCharts={summaryCharts} />*/}
      <ChartsBlocks chartsData={chartsData} isLoading={isSpecificChartsLoading} selectedDateRange={selectedDateRange} />
    </>
  );
};

const ChartsBlocks = ({ chartsData, isLoading, selectedDateRange }) => {
  const BLOCK_NAMES = ['Productivity & Quality', 'Performance', 'System Performance', 'Earnings'];

  return (
    <div className='grid grid-cols-2 gap-4 h-full mt-2 relative auto-rows-fr'>
      {isLoading ? (
        <Loader category='transparent' />
      ) : (
        <>
          {[1, 2, 3, 4].map((block) => {
            const blockChartsData = chartsData.filter((c) => c.chart.block === block);
            return (
              <div key={block} className='flex flex-col border-1 border-perception-black-800 text-white'>
                <div className="bg-perception-black-800 p-4">
                  {BLOCK_NAMES[block - 1]}
                </div>
                <div className="h-full flex items-center justify-around gap-8 p-4">
                  {blockChartsData.sort((a, b) => b.id - a.id).map((chartData) => {
                    let value = chartData.value;
                    // Apply coefficient in demo mode
                    if (process.env.REACT_APP_PRODUCTION_MODE === '2' && chartData.type.startsWith('value')){
                      if (selectedDateRange.id === DATE_RANGES.WEEK) value *= 5;
                      else if (selectedDateRange.id === DATE_RANGES.MONTH) value *= 20;
                      else if (selectedDateRange.id === DATE_RANGES.YEAR) value *= 220;
                    }
                    return (
                      <>
                      {
                        chartData.type === 'line' ?
                          <BlockLineChart chartData={chartData}/>
                          :
                        <div key={chartData.chartData.id} className="w-1/4 mx-8">
                          <div className="rounded-md bg-perception-black-800 flex justify-center p-4">
                            <GenericChart type={chartData.type} value={value} />
                          </div>
                          <div className='text-center mt-4 font-semibold h-[50px] flex items-center justify-center text-center'>{chartData.name}</div>
                        </div>
                      }
                      </>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </>
      )}
    </div>
  );
};

const EmptyChartBlock = () => {
  return <div className='border-perception-black-800 border-2 border-dashed' />;
};
